import { useEffect, useRef } from "react";
import { Toggle } from "@v0y4ge/jrni-ui";
import {
  listMessages, updateMessage, getMessagingSettings, updateMessagingSettings, apiRaw,
} from "@v0y4ge/api-client";
import {
  MessagingSenderSettingsPatchInput,
  type MessagingSenderSettings,
  studioMessageGroupFor,
  studioMessageJourneyMomentKeyFromAssetId,
  studioMessageJourneyMomentSupportsEnabled,
  type StudioMessage,
} from "@v0y4ge/api-contracts";
import { AUTO_MESSAGE_CHANNELS, isAutoMessageChannel, type AutoMessageChannel } from "./messaging-tabs";
import { studioRequestOptions } from "../../lib/studio-config";

export type RowLineage = "appointment_comms" | "journey_moment";
export type GroupId = "appointments" | "events" | "queues";
/** The customer-facing channels this surface tabs over. */
export type MessageChannel = AutoMessageChannel;

export interface MessageRow {
  /** Server id. For appointment rows this is the compiler's `mast_*` asset id. */
  id: string;
  /** Server-provided display name. Never a client-side constant. */
  name: string;
  lineage: RowLineage;
  groupId: GroupId;
  /** Server trigger, e.g. `appointment.reminder`, `queue.nearly_up`. */
  trigger: string;
  /** Nullable. Present on appointment cadence steps; identifies the step. */
  offsetMinutes: number | null;
  /** Journey-moment key where the lineage provides one — used for copy only. */
  momentKey: string | null;
  /** The tab this row was read for. */
  channel: MessageChannel;
  /**
   * Set ONLY when this one row is the switch for more than one channel — the
   * pre-JAI-1619 journey-moment shape. `null` means the row owns its channel
   * alone, which is what every appointment row and every post-kit row is.
   */
  sharedChannels: MessageChannel[] | null;
  enabled: boolean;
}

export interface SenderView {
  fromName: string | null;
  replyTo: string | null;
  /** Resolved, READ-ONLY. `null` renders "Not available" — never a guess. */
  fromAddress: string | null;
  fromAddressSource: "platform" | "tenant_credential" | null;
}

/** Rows per channel, in `AUTO_MESSAGE_CHANNELS` order. A channel absent from
 *  the map has no rows and therefore no tab. */
export type ChannelRows = Partial<Record<MessageChannel, MessageRow[]>>;

export interface MessagesSource {
  /** One paginated read per candidate channel; must exhaust pagination. */
  loadRows(): Promise<ChannelRows>;
  /** Resolves with the authoritative server row; rejects with a coded error. */
  setEnabled(id: string, enabled: boolean, channel: MessageChannel): Promise<MessageRow>;
  loadSender(): Promise<SenderView>;
  /** Sends ONLY the field being changed. Never fromAddress/fromAddressSource. */
  saveSender(patch: { fromName?: string | null; replyTo?: string | null }): Promise<SenderView>;
}

/* ────────────────────────────────────────────────────────────────────────────
   LIVE SOURCE
   ──────────────────────────────────────────────────────────────────────────── */

const PAGE_LIMIT = 100;
/** A stop so a paginating read can never spin forever on a bad `hasMore`. */
const MAX_PAGES = 50;

/**
 * Server projection → row, or `null` when the row does not belong on this page.
 * Returning null (rather than throwing) is deliberate, and it is the contract's
 * own stated rule for `journey_moment`: one unrecognised row must not take down
 * the list page it appears on.
 */
/** A row's OWN channels: the singular `channel` a post-JAI-1619 projection
 *  carries, else the `channels[]` every current row carries. */
function declaredChannels(m: StudioMessage): MessageChannel[] {
  const single = (m as { channel?: unknown }).channel;
  if (typeof single === "string" && isAutoMessageChannel(single)) return [single];
  const many: unknown = (m as { channels?: unknown }).channels;
  return Array.isArray(many) ? many.filter(isAutoMessageChannel) : [];
}

/** A row id with any CHANNEL SUFFIX removed.
 *
 *  JAI-1619 addresses a journey moment per channel — `asset_<key>:sms` beside
 *  `asset_<key>` — and `studioMessageJourneyMomentKeyFromAssetId` answers `null`
 *  for the suffixed spelling. Left unstripped that silently disabled everything
 *  downstream of the moment key: the row lost its `momentKey`, fell back to
 *  keying on `trigger`, merged with nothing, and skipped the contract's
 *  no-own-switch exclusion entirely. The suffix is presentation of identity, not
 *  identity, so it comes off before the lookup. */
function assetIdWithoutChannel(id: string): string {
  return String(id).replace(/:(email|sms|whatsapp|push|webhook)$/, "");
}

export function toMessageRow(m: StudioMessage, channel: MessageChannel): MessageRow | null {
  const lineage = m.lineage;
  if (lineage !== "appointment_comms" && lineage !== "journey_moment") return null;

  // The moment a journey row names. `…KeyFromAssetId` is the contract's own
  // reader for the `asset_<key>` ids this surface addresses moments by — read
  // through `assetIdWithoutChannel`, so a channel-addressed row resolves to the
  // same moment as its email sibling and is held to the same exclusions.
  const momentKey = lineage === "journey_moment"
    ? studioMessageJourneyMomentKeyFromAssetId(assetIdWithoutChannel(m.id))
    : null;

  // Moments with no switch of their own are excluded by the CONTRACT's list
  // (STUDIO_MESSAGE_JOURNEY_MOMENTS_WITHOUT_OWN_SWITCH), not by a local copy:
  // appointment_confirmation / appointment_reminder share the appointment plan
  // and their customer-facing rows are the compiled appointment_comms rows.
  if (momentKey && !studioMessageJourneyMomentSupportsEnabled(momentKey)) return null;

  // ABSENT `enabled` means "this lineage has no such switch" — explicitly NOT
  // false. A row with no switch has nothing for this page to render, so it is
  // dropped rather than shown as off.
  if (typeof m.enabled !== "boolean") return null;

  // Group is DERIVED and read-only. Prefer the server's own derivation; fall
  // back to the contract's `studioMessageGroupFor` rather than re-deriving.
  const group = m.group ?? studioMessageGroupFor(lineage, momentKey);
  const groupId = group.id === "appointments" ? "appointments"
    : group.id === "events" ? "events"
      : group.id === "queues" ? "queues"
        : null;
  // An unrecognised journey key groups as `journey_moment` rather than throwing;
  // that floor group is not this page's, so the row is skipped here.
  if (!groupId) return null;

  // `trigger` is appointment_comms-only; a moment row is identified by its key.
  const trigger = m.trigger ?? momentKey;
  if (!trigger) return null;

  // More than one channel on ONE row means one switch drives all of them —
  // the pre-JAI-1619 journey-moment shape, which the row says so about rather
  // than pretending to be per-channel.
  const declared = declaredChannels(m);
  const sharedChannels = declared.length > 1 ? declared : null;

  return {
    id: m.id,
    name: m.name,
    lineage,
    groupId,
    trigger,
    offsetMinutes: m.offsetMinutes ?? null,
    momentKey,
    channel,
    sharedChannels,
    enabled: m.enabled,
  };
}

const LIST_PATH = "/v1/admin/messages";
const listQuery = (channel: MessageChannel, page: number) =>
  `${LIST_PATH}?kind=message&channel=${channel}&lineage=all&page=${page}&limit=${PAGE_LIMIT}`;

async function loadValidatedRows(channel: MessageChannel): Promise<StudioMessage[]> {
  const collected: StudioMessage[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await listMessages({
      kind: "message", channel, lineage: "all", page, limit: PAGE_LIMIT,
    });
    const batch = res.messages;
    collected.push(...batch);
    if (!res.pagination.hasMore || batch.length === 0) break;
  }
  return collected;
}

/**
 * Row-at-a-time read of the SAME endpoint, used only when the validated read
 * rejected the page.
 *
 * `listMessages` validates the list as ONE envelope, so a single stored row
 * carrying a pre-contract value — a legacy `status: "live"`, an `emailDesign`
 * built from the old block vocabulary — fails the parse and the screen loses
 * all fifteen rows to a wall of Zod text. That is exactly the failure
 * `toMessageRow` is written to refuse: one unreadable row must not take down
 * the list page it appears on. Here the envelope is read raw and each row is
 * admitted on its own; a row this page cannot read is skipped and logged, not
 * rendered. A genuine transport or authorization failure still throws, so the
 * error state keeps meaning "the read failed".
 */
async function loadTolerantRows(channel: MessageChannel): Promise<MessageRow[]> {
  const rows: MessageRow[] = [];
  let skipped = 0;
  for (let page = 1; page <= MAX_PAGES; page++) {
    const res = await apiRaw<{
      messages?: unknown[]; data?: unknown[]; pagination?: { hasMore?: boolean };
    }>(listQuery(channel, page), undefined, "listStudioMessages");
    const batch = (res?.messages ?? res?.data ?? []) as unknown[];
    batch.forEach((raw) => {
      const row = rowFromUnknown(raw, channel);
      if (row) rows.push(row); else skipped += 1;
    });
    if (!res?.pagination?.hasMore || batch.length === 0) break;
  }
  if (skipped) console.warn(`automatic messages (${channel}): ${skipped} row(s) this page cannot read were skipped.`);
  return rows;
}

/** `toMessageRow` reads only id/name/lineage/enabled/group/trigger/offsetMinutes —
 *  none of the fields a legacy row fails validation on — so an unvalidated row
 *  can be offered to it behind these guards. */
function rowFromUnknown(raw: unknown, channel: MessageChannel): MessageRow | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.id !== "string" || typeof m.name !== "string") return null;
  try {
    return toMessageRow(m as unknown as StudioMessage, channel);
  } catch (e) {
    console.warn("automatic messages: row skipped as unreadable:", m.id, e);
    return null;
  }
}

async function loadChannelRows(channel: MessageChannel): Promise<MessageRow[]> {
  let collected: StudioMessage[];
  try {
    collected = await loadValidatedRows(channel);
  } catch (e) {
    console.warn(`automatic messages (${channel}): the list failed contract validation; reading rows individually.`, e);
    return loadTolerantRows(channel);
  }
  // Filter AFTER every page is in hand — never treat page 1 as the inventory.
  return collected
    .map((m) => toMessageRow(m, channel))
    .filter((r): r is MessageRow => r !== null);
}

/**
 * One read per candidate channel. A channel that returns no renderable row is
 * left OUT of the map, which is what removes its tab — an empty tab for a
 * channel this tenant does not send on is never shown.
 *
 * The reads run together: they are independent queries and the page needs all
 * of them before it can name its tabs.
 */
async function loadAllChannelRows(): Promise<ChannelRows> {
  const results = await Promise.all(
    AUTO_MESSAGE_CHANNELS.map((channel) => loadChannelRows(channel)),
  );
  const out: ChannelRows = {};
  AUTO_MESSAGE_CHANNELS.forEach((channel, i) => {
    if (results[i].length > 0) out[channel] = results[i];
  });
  return out;
}

function toSenderView(sender: MessagingSenderSettings): SenderView {
  return {
    fromName: sender.fromName,
    replyTo: sender.replyTo,
    fromAddress: sender.fromAddress,
    fromAddressSource: sender.fromAddress ? sender.fromAddressSource : null,
  };
}

export const LIVE_SOURCE: MessagesSource = {
  loadRows: loadAllChannelRows,
  async setEnabled(id, enabled, channel) {
    const updated = await updateMessage(id, { enabled });
    const row = toMessageRow(updated, channel);
    if (!row) throw new Error("The server returned a row this page cannot display.");
    return row;
  },
  async loadSender() {
    // `messaging` is required on the response; `data` is only an optional alias.
    const res = await getMessagingSettings(studioRequestOptions("messaging-automatic"));
    return toSenderView(res.messaging.sender);
  },
  async saveSender(patch) {
    // The PATCH nests `sender` because the GET nests it — the contract is
    // explicit that the two shapes match.
    const res = await updateMessagingSettings({ sender: patch }, studioRequestOptions("messaging-automatic"));
    return toSenderView(res.messaging.sender);
  },
};

export class NotWiredError extends Error {
  code = "MESSAGES_NOT_WIRED";
  constructor() { super("Messages API is not wired in this build."); }
}

/** Retained for the state gallery and for tests that must not hit the network. */
export const UNWIRED_SOURCE: MessagesSource = {
  loadRows: () => Promise.reject(new NotWiredError()),
  setEnabled: () => Promise.reject(new NotWiredError()),
  loadSender: () => Promise.reject(new NotWiredError()),
  saveSender: () => Promise.reject(new NotWiredError()),
};

/* ────────────────────────────────────────────────────────────────────────────
   COPY + TIMING — derived from the server row, behind i18n keys.
   The 15-row table is a CATALOGUE, not a whitelist: `purposeLine` falls back to
   nothing rather than dropping a row, and `timingLine` derives from
   trigger/offsetMinutes so an unknown cadence still prints correct timing.
   ──────────────────────────────────────────────────────────────────────────── */

/** Identity for copy lookup. Appointment steps key on trigger+offset. */
export function rowKey(row: MessageRow): string {
  if (row.lineage === "journey_moment") return row.momentKey ?? row.trigger;
  return row.offsetMinutes === null ? row.trigger : `${row.trigger}@${row.offsetMinutes}`;
}

const PURPOSE: Record<string, [key: string, fallback: string]> = {
  "appointment.confirmed": ["messaging.auto.purpose.apptConfirmed", "Confirms that the appointment was booked."],
  "appointment.reminder@1440": ["messaging.auto.purpose.apptReminder24h", "Reminds the customer about tomorrow's appointment."],
  "appointment.reminder@60": ["messaging.auto.purpose.apptReminder1h", "Gives the customer a last-minute appointment reminder."],
  "appointment.rescheduled": ["messaging.auto.purpose.apptRescheduled", "Tells the customer that the appointment time changed."],
  "appointment.cancelled": ["messaging.auto.purpose.apptCancelled", "Confirms that the appointment was cancelled."],
  "appointment.follow_up@120": ["messaging.auto.purpose.apptFollowUp", "Follows up after the customer's visit."],
  event_confirmation: ["messaging.auto.purpose.eventConfirmation", "Confirms the customer's event place."],
  event_reminder_7d: ["messaging.auto.purpose.eventReminder7d", "Gives the customer time to plan for the event."],
  event_reminder_1d: ["messaging.auto.purpose.eventReminder1d", "Gives the customer a final reminder for tomorrow's event."],
  event_waitlist: ["messaging.auto.purpose.eventWaitlist", "Confirms that a place opened and the waitlisted customer is registered."],
  event_cancelled: ["messaging.auto.purpose.eventCancelled", "Tells the customer that the registered event was cancelled."],
  queue_join: ["messaging.auto.purpose.queueJoin", "Confirms that the customer joined the queue."],
  queue_nearly_up: ["messaging.auto.purpose.queueNearlyUp", "Tells the customer to return because their turn is approaching."],
  queue_called: ["messaging.auto.purpose.queueCalled", "Tells the customer that their turn is ready."],
  queue_delayed: ["messaging.auto.purpose.queueDelayed", "Tells the customer that the queue is delayed while holding their place."],
};

/**
 * Anchor direction. `appointment.follow_up` counts FORWARD from completion;
 * every other offset cadence counts BACKWARD from its anchor. Direction is
 * carried by the trigger, never by the sign of `offsetMinutes`.
 */
function isForward(trigger: string): boolean {
  return trigger.endsWith(".follow_up");
}

function anchorFallback(groupId: GroupId): string {
  return groupId === "events" ? "event" : groupId === "queues" ? "queue" : "appointment";
}

export function timingLine(row: MessageRow, t: (k: string, f: string) => string): string {
  if (row.offsetMinutes === null || row.offsetMinutes === undefined) {
    return t("messaging.auto.timing.onEvent", "When {trigger} occurs.").replace("{trigger}", row.trigger);
  }
  const formatted = new Intl.NumberFormat().format(Math.abs(row.offsetMinutes));
  const anchor = anchorFallback(row.groupId);
  if (isForward(row.trigger)) {
    return t("messaging.auto.timing.after", "{minutes} minutes after {anchor} completion ({trigger}).")
      .replace("{minutes}", formatted).replace("{anchor}", anchor).replace("{trigger}", row.trigger);
  }
  return t("messaging.auto.timing.before", "{minutes} minutes before the {anchor}.")
    .replace("{minutes}", formatted).replace("{anchor}", anchor);
}

export function purposeLine(row: MessageRow, t: (k: string, f: string) => string): string | null {
  const entry = PURPOSE[rowKey(row)];
  // Unknown row: no invented sentence. Name and timing still carry it.
  return entry ? t(entry[0], entry[1]) : null;
}

/* ────────────────────────────────────────────────────────────────────────────
   MERGE — one row per MESSAGE, carrying its channels (v1.2, 2026-09-16)

   The server projects one row per (message, channel); the page shows one row per
   message. `rowKey` is already the message's identity for copy lookup — the
   journey-moment key, or trigger@offset for an appointment cadence step — so it
   is the merge key too, qualified by lineage so a moment and a cadence step can
   never collide.

   Order is FIRST-SEEN order within each group, taken from the channel reads in
   `AUTO_MESSAGE_CHANNELS` order. That means the list is ordered by the email
   read (every tenant's fullest channel) and a message that exists only on SMS
   appends after it, rather than the order changing with whichever read returned
   first.
   ──────────────────────────────────────────────────────────────────────────── */

export interface MessageGroup {
  /** `lineage:rowKey` — stable across channels and across reloads. */
  key: string;
  /** Display name. The email variant's name when there is one: the channels
   *  carry their own names and email is the one every tenant has. */
  name: string;
  groupId: GroupId;
  lineage: RowLineage;
  /** Any one row, for the copy helpers (purpose/timing are per message). */
  sample: MessageRow;
  byChannel: Partial<Record<MessageChannel, MessageRow>>;
}

export function messageGroupKey(row: MessageRow): string {
  /* THE OFFSET IS PART OF THE IDENTITY (CD docket: "editing one must never
     change either of the others"). `rowKey` is the COPY key and for a journey
     moment it is the moment alone, so the 7-day and 1-day event reminders share
     it — merging on that collapsed two messages into one and gave one of them
     the other's SMS record. The merge key therefore carries `offsetMinutes`
     whenever the row has one, for both lineages. An appointment's `rowKey`
     already ends in `@<offset>`, so it is not appended twice — the key is for
     reading as well as for matching. */
  const key = rowKey(row);
  const offset = row.offsetMinutes === null || row.offsetMinutes === undefined ? "" : `@${row.offsetMinutes}`;
  return `${row.lineage}:${key}${key.endsWith(offset) ? "" : offset}`;
}

export function mergeChannelRows(byChannel: ChannelRows): MessageGroup[] {
  const out = new Map<string, MessageGroup>();
  for (const channel of AUTO_MESSAGE_CHANNELS) {
    for (const row of byChannel[channel] ?? []) {
      const key = messageGroupKey(row);
      const existing = out.get(key);
      if (!existing) {
        out.set(key, {
          key, name: row.name, groupId: row.groupId, lineage: row.lineage,
          sample: row, byChannel: { [channel]: row },
        });
        continue;
      }
      existing.byChannel[channel] = row;
      // Email owns the display name where both exist; see the note above.
      if (channel === "email") existing.name = row.name;
    }
  }
  return [...out.values()];
}

/** Channels HIDDEN from this page (2026-09-16, operator): WhatsApp comes off the
 *  grid for now. Hidden, NOT removed — the read still covers it, the merged rows
 *  still carry it, nothing is switched off, and deleting the entry below is the
 *  whole un-hide. Same posture as `HIDDEN_TABS` in messaging-visibility. */
const HIDDEN_CHANNELS: MessageChannel[] = ["whatsapp"];

/** The channel columns to draw: what this tenant carries ANYWHERE, in
 *  `AUTO_MESSAGE_CHANNELS` order, minus the hidden ones. A channel no message
 *  has a row for gets no column — an all-"not available" column asks the
 *  operator to read nothing. */
export function channelColumns(groups: MessageGroup[]): MessageChannel[] {
  return AUTO_MESSAGE_CHANNELS
    .filter((c) => !HIDDEN_CHANNELS.includes(c))
    .filter((c) => groups.some((g) => g.byChannel[c]));
}

export function channelLabel(channel: MessageChannel, t: (k: string, f: string) => string): string {
  return channel === "sms" ? t("messaging.auto.channel.sms", "SMS")
    : channel === "whatsapp" ? t("messaging.auto.channel.whatsapp", "WhatsApp")
      : t("messaging.auto.channel.email", "Email");
}

/** A journey-moment switch is a plan-binding write and needs flows.write too. */
export function rowWriteCapabilities(row: MessageRow): string[] {
  return row.lineage === "journey_moment" ? ["campaigns.write", "flows.write"] : ["campaigns.write"];
}

/* ────────────────────────────────────────────────────────────────────────────
   SENDER VALIDATION — the SHARED contract schema, nothing local.
   The schema does the raw C0/C1/DEL check before any trim, then trims; '' is a
   documented clear for fromName and invalid for replyTo. We canonicalize ONLY
   after a successful parse, per the handover's worked example.
   ──────────────────────────────────────────────────────────────────────────── */

export type SenderField = "fromName" | "replyTo";

export function checkSenderField(
  field: SenderField,
  raw: string,
): { ok: true; value: string | null } | { ok: false; message: string } {
  const parsed = MessagingSenderSettingsPatchInput.safeParse({ [field]: raw });
  if (!parsed.success) {
    const issue = parsed.error.issues.find((i) => i.path[0] === field) ?? parsed.error.issues[0];
    return { ok: false, message: issue?.message ?? "Invalid value." };
  }
  const value = (parsed.data as Record<string, unknown>)[field];
  if (typeof value !== "string") return { ok: true, value: null };
  // Documented canonicalization: an empty display name IS the null clear.
  if (field === "fromName" && value === "") return { ok: true, value: null };
  return { ok: true, value };
}

/* ui-raw ACCESSIBILITY SHIM — `Toggle` DROPS `aria-label` (2026-09-16).
 *
 * The design-system `Toggle` renders `<span role="switch" aria-checked>` and
 * forwards neither `aria-label` nor `aria-labelledby`, so the label this file
 * passed it never reached the DOM. Under v1.1 that was survivable — one switch
 * per row, named by the row's text, with the channel named by the active tab.
 * After the merge there are three switches per row that differ ONLY by column,
 * so an unnamed switch makes channel identity — the whole point of the redesign
 * — unavailable to a screen reader or voice control.
 *
 * So the name is rendered here, as real (visually hidden) text, and bound to the
 * switch node by `aria-labelledby` after mount. Not `aria-label` on a wrapper:
 * the name has to sit on the element carrying `role="switch"`, and that element
 * belongs to the component. Reverts to a plain `<Toggle aria-label>` the day the
 * design system forwards it — FLAGGED to the design-system line alongside the
 * borderless icon-button request.
 */
export function ChannelSwitch({
  row, label: labelText, disabled, onChange,
}: {
  row: MessageRow;
  label: string;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  const host = useRef<HTMLSpanElement | null>(null);
  const labelId = `automail-switch-${row.id}`.replace(/[^a-zA-Z0-9_-]/g, "-");
  useEffect(() => {
    const node = host.current?.querySelector('[role="switch"]');
    if (node) node.setAttribute("aria-labelledby", labelId);
  }, [labelId]);
  return (
    <span ref={host} style={{ display: "inline-flex", alignItems: "center" }}>
      <span
        id={labelId}
        style={{
          position: "absolute", width: 1, height: 1, margin: -1, padding: 0,
          overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0,
        }}
      >
        {labelText}
      </span>
      <Toggle on={row.enabled} disabled={disabled} onChange={onChange} />
    </span>
  );
}

/** Rows across every channel bucket, so a shared moment row is updated in all
 *  the tabs that show it rather than only the one it was clicked in. */
export function patchRow(byChannel: ChannelRows, id: string, patch: (row: MessageRow) => MessageRow): ChannelRows {
  const out: ChannelRows = {};
  for (const channel of AUTO_MESSAGE_CHANNELS) {
    const rows = byChannel[channel];
    if (rows) out[channel] = rows.map((r) => (r.id === id ? patch(r) : r));
  }
  return out;
}
